// A file in the open league format, brought in (DIRECTION A13,
// OPEN_LEAGUE_FORMAT.md, PLAN-2026-10-18 §13.1): read on the device and
// checked against the format before anything is sent, so a wrong file is
// said at once; a league's file then starts a league here.

import {
  ImportLeagueResult,
  OpenLeagueFile,
  type ImportLeagueResult as ImportLeagueResultView,
} from '@fsp/domain';
import { messageOf, send } from './api';
import { deviceZone } from './hooks';

export type ReadOutcome = { ok: true; file: OpenLeagueFile } | { ok: false; message: string };

/** The largest file the server takes (`POST /api/leagues/import`). */
const MAX_BYTES = 4 * 1024 * 1024;

export async function readLeagueFile(picked: File): Promise<ReadOutcome> {
  if (picked.size > MAX_BYTES)
    return { ok: false, message: 'That file is larger than a league’s file can be (4 MB).' };
  let raw: unknown;
  try {
    raw = JSON.parse(await picked.text());
  } catch {
    return { ok: false, message: 'That file is not JSON, so it is not a league’s file.' };
  }
  const parsed = OpenLeagueFile.safeParse(raw);
  if (!parsed.success)
    return { ok: false, message: 'That file is not a league or a team in the open league format.' };
  return { ok: true, file: parsed.data };
}

/** The season a league made today plays, as the create form decides it. */
function seasonNow(now: number): number {
  const at = new Date(now);
  return at.getMonth() < 2 ? at.getFullYear() - 1 : at.getFullYear();
}

/** `now`: the screen's moment, for the season a league made today plays. */
export async function importLeague(
  file: OpenLeagueFile,
  myTeam: string,
  now: number,
): Promise<{ ok: true; result: ImportLeagueResultView } | { ok: false; message: string }> {
  const reply = await send(
    'POST',
    '/leagues/import',
    { file, myTeam, season: seasonNow(now), timeZone: deviceZone() },
    ImportLeagueResult,
  );
  if (reply.status !== 'ok')
    return {
      ok: false,
      message: messageOf(reply, 'That league could not be brought in. Try again.'),
    };
  return { ok: true, result: reply.data };
}
